globalThis.World = class World {
  constructor() {
    globalThis.WORLD = this;
    this.players = new Map();
    this.connections = new Map();
    this.loading = false;
    this.position = new Vector2();
    this.size = new Vector2();
    this.scale = 1;
    this.div = document.createElement("div");
    this.div.id = "world";
    document.body.append(this.div);
    this.setScale(0.5);
    this.setSize(16, 16);
    this.setPosition(0, 0);
  }

  remove() {
    this.removeAllPlayers();
    this.clear();
    this.div.remove();
    delete globalThis.WORLD;
  }

  removeAllPlayers() {
    for (const id of this.players.keys()) this.removePlayer(id);
  }

  connect(profile) {
    this.connections.set(profile.id, profile);
    dispatchEvent(new Event("connections"));
  }

  disconnect(id) {
    this.connections.delete(id);
    dispatchEvent(new Event("connections"));
  }

  updateBlock(update) {
    const { x, y } = update.position;
    const block = this.blocks[x] && this.blocks[x][y];
    if (!block) return;
    block.update(update);
  }

  updatePlayer(update) {
    const { u, i } = update;
    const player = this.players.get(u + i);
    if (!player) return;
    player.update(update);
  }

  clear() {
    for (let x = -1; x <= this.size.x; x++)
      for (let y = -1; y <= this.size.y; y++) this.removeAt(x, y);
  }

  getOwnPlayersDistance() {
    let distance = 0;
    const own_players = this.getOwnPlayers();
    for (const player of own_players) {
      for (const other of own_players) {
        if (player === other) continue;
        distance = Math.max(distance, player.position.distance(other.position));
      }
    }
    return distance;
  }

  getOwnPlayersCenter() {
    return Vector2.avg(this.getOwnPlayers().map((plr) => plr?.position));
  }

  getOwnPlayers() {
    const own_players = [];
    for (const player of this.players.values())
      if (player.uid === globalThis.STORE.profile.id) own_players.push(player);
    return own_players;
  }

  hasOwnPlayers() {
    for (const player of this.players.values())
      if (player.uid === globalThis.STORE.profile.id) return true;
  }

  addPlayer(uid, index) {
    this.players.get(uid + index)?.remove();
    let player;
    if (uid === globalThis.STORE.profile.id) {
      const keys = globalThis.PLAYER_CONTROLS[index];
      player = new Player(PlayerConfiguration.ofKeys(...keys, index));
    } else player = new Player({ index, remote: true });
    this.players.set(uid + index, player);
    this.div.append(player.div);
    dispatchEvent(new CustomEvent("addplayer", { detail: [uid, index] }));
  }

  removePlayer(uid, index) {
    this.players.get(uid + index)?.remove();
    this.players.delete(uid + index);
    dispatchEvent(new CustomEvent("removeplayer", { detail: [uid, index] }));
  }

  addBlock(block) {
    const { x, y } = block.position;
    if (this.blocks[x][y]) this.removeBlock(this.blocks[x][y], false);
    this.blocks[x][y] = block;
    this.div.append(block.div);
    if (!this.loading)
      dispatchEvent(new CustomEvent("addblock", { detail: block }));
  }

  removeBlock(block, dispatch = true) {
    const { x, y } = block;
    if (this.blocks[x]) delete this.blocks[x][y];
    block?.remove();
    if (!this.loading && block && dispatch)
      dispatchEvent(new CustomEvent("removeblock", { detail: block }));
  }

  removeAt(x, y) {
    const block = this.blocks[x][y];
    delete this.blocks[x][y];
    block?.remove();
    if (!this.loading && block)
      dispatchEvent(new CustomEvent("removeblock", { detail: block }));
  }

  setSize(x, y) {
    this.size.x = x;
    this.size.y = y;
    this.ensureBlockBuffers();
    this.cleanBlockBuffers();
    this.renderSize();
    dispatchEvent(new Event("worldsize"));
  }

  ensureBlockBuffers() {
    this.blocks = this.blocks || [];
    for (let i = -1; i <= this.size.x; i++)
      this.blocks[i] = this.blocks[i] || [];
  }

  cleanBlockBuffers() {
    for (let x = -1; x <= this.size.x; x++) {
      const file = this.blocks[x];
      for (let y = this.size.y + 1; y < file.length; y++) this.removeAt(x, y);
    }
    for (let x = this.size.x + 1; x < this.blocks.length; x++) {
      const file = this.blocks[x];
      for (let y = -1; y < file.length; y++) this.removeAt(x, y);
    }
  }

  renderSize() {
    this.div.style.setProperty("--size-x", this.size.x);
    this.div.style.setProperty("--size-y", this.size.y);
  }

  increaseScale() {
    this.setScale(this.scale + globalThis.SCALE_STEP);
  }

  decreaseScale() {
    this.setScale(this.scale - globalThis.SCALE_STEP);
  }

  setScale(scale) {
    this.scale = scale;
    this.clipScale();
    this.renderScale();
  }

  clipScale() {
    this.scale = Math.min(this.scale, globalThis.SCALE_MAX);
    this.scale = Math.max(this.scale, globalThis.SCALE_MIN);
  }

  renderScale() {
    this.div.style.setProperty("--scale", this.scale);
  }

  setPosition(x, y) {
    this.position.x = x;
    this.position.y = y;
    this.renderPosition();
  }

  renderPosition() {
    this.div.style.setProperty("--x", this.position.x);
    this.div.style.setProperty("--y", this.position.y);
  }

  toBase64() {
    const rows = [];
    for (let y = -1; y <= this.size.y; y++) {
      let row = "";
      for (let x = -1; x <= this.size.x; x++) {
        const block = this.blocks[x][y];
        if (block?.index < 64) row += globalThis.BASE64[block.index];
        else row += " ";
      }
      rows.push(row);
    }
    return rows.join("\n");
  }

  applyBase64(data) {
    this.loading = true;
    const base64 = globalThis.BASE64.split("");
    const rows = data.split("\n");
    let width = 0;
    for (const row of rows) width = Math.max(width, row.length);
    this.setSize(width - 2, rows.length - 2);
    for (let y = 0; y < rows.length; y++) {
      const row = rows[y];
      for (let x = 0; x < row.length; x++) {
        const char = row[x];
        const index = base64.findIndex((c) => c === char);
        if (index !== -1) this.addBlock(new Block(x - 1, y - 1, index));
        else this.removeAt(x - 1, y - 1);
      }
    }
    this.loading = false;
  }
};
